<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'

const props = defineProps<{
  entries: string[]
}>()

const hostRef = ref<HTMLElement | null>(null)
const visible = ref(false)
const tipStyle = ref<{ top: string; left: string }>({ top: '0px', left: '0px' })

function updatePosition() {
  const el = hostRef.value
  if (!el) return
  const r = el.getBoundingClientRect()
  tipStyle.value = {
    top: `${r.bottom + 8}px`,
    left: `${r.left}px`,
  }
}

function show() {
  updatePosition()
  visible.value = true
}

function hide() {
  visible.value = false
}

function onScrollOrResize() {
  if (visible.value) updatePosition()
}

onMounted(() => {
  window.addEventListener('scroll', onScrollOrResize, true)
  window.addEventListener('resize', onScrollOrResize)
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScrollOrResize, true)
  window.removeEventListener('resize', onScrollOrResize)
})
</script>

<template>
  <span
    ref="hostRef"
    class="fu-preferred-time-host"
    @mouseenter="show"
    @mouseleave="hide"
    @focusin="show"
    @focusout="hide"
  >
    <slot />
    <Teleport to="body">
      <div
        v-show="visible"
        class="fu-preferred-time-tip fu-preferred-time-tip--overlay"
        role="tooltip"
        :style="tipStyle"
      >
        <div class="fu-preferred-time-tip-title">Preferred Time:</div>
        <ul v-if="props.entries.length" class="fu-preferred-time-tip-list">
          <li v-for="(pt, pti) in props.entries" :key="pti">{{ pt }}</li>
        </ul>
        <p v-else class="fu-preferred-time-tip-empty">No preferred times on file.</p>
      </div>
    </Teleport>
  </span>
</template>

<style scoped>
.fu-preferred-time-host {
  position: relative;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

.fu-preferred-time-tip--overlay {
  position: fixed;
  z-index: 10000;
  box-sizing: border-box;
  width: 169px;
  height: 120px;
  padding: 10px 12px 12px;
  background: #fef39c;
  border-radius: 8px;
  box-shadow: 0 8px 16px 0 rgba(0, 0, 0, 0.1);
  text-align: left;
  pointer-events: none;
  margin: 0;
  overflow-x: hidden;
  overflow-y: auto;
}

.fu-preferred-time-tip-title {
  box-sizing: border-box;
  width: 100%;
  max-width: 151px;
  min-height: 20px;
  margin: 0 0 6px;
  padding: 0;
  font-family: SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 0;
  color: #484848;
  text-align: left;
  white-space: nowrap;
}

.fu-preferred-time-tip-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.fu-preferred-time-tip-list li,
.fu-preferred-time-tip-empty {
  box-sizing: border-box;
  width: 100%;
  max-width: 151px;
  min-height: 20px;
  height: auto;
  margin: 0;
  padding: 0;
  font-family: SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0;
  color: #484848;
  text-align: left;
  white-space: normal;
  overflow-wrap: break-word;
  word-break: break-word;
}
</style>
